import { useCallback, useMemo } from "react";
import {
  MorphemeDetailsTileUi,
  type MorphemeDetailsTileUiProps,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.details";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

export type MorphemeDetailsTileLinkProps = Omit<
  MorphemeDetailsTileUiProps,
  "isLoading" | "isSelected" | "onLongPress" | "onPress"
> & {
  isNavigating?: boolean;
  /** Shared by a virtualized list; standalone links keep their own navigator. */
  onNavigate?: (graphemeCard: MorphemeDetailsTileUiProps["graphemeCard"]) => void;
};

function ResolvedMorphemeDetailsTileLink({
  graphemeCard,
  isNavigating,
  onGraphemePress,
  onNavigate,
  ...morphemeDetailsTileLinkProps
}: MorphemeDetailsTileLinkProps & {
  onNavigate: NonNullable<MorphemeDetailsTileLinkProps["onNavigate"]>;
}) {
  const selectionItems = useMemo(
    () => [morphemeSelectionItem(graphemeCard.front)],
    [graphemeCard.front],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const handlePress = useCallback(
    (graphemeCard: MorphemeDetailsTileUiProps["graphemeCard"]) => {
      if (consumePress()) {
        return;
      }
      onGraphemePress?.(graphemeCard);
      onNavigate(graphemeCard);
    },
    [consumePress, onGraphemePress, onNavigate],
  );

  return (
    <MorphemeDetailsTileUi
      graphemeCard={graphemeCard}
      isLoading={isNavigating}
      isSelected={isSelected}
      onLongPress={onLongPress}
      onPress={handlePress}
      {...morphemeDetailsTileLinkProps}
    />
  );
}

function SelfNavigatingMorphemeDetailsTileLink(props: MorphemeDetailsTileLinkProps) {
  const { push } = useBrowseMorphemePageNavigator();
  const mutate = push.mutate;
  const onNavigate = useCallback(
    (card: MorphemeDetailsTileUiProps["graphemeCard"]) => {
      mutate({ morpheme: card.front, params: { view: "mnemonics" } });
    },
    [mutate],
  );
  return (
    <ResolvedMorphemeDetailsTileLink
      {...props}
      isNavigating={push.isPending && push.variables?.morpheme === props.graphemeCard.front}
      onNavigate={onNavigate}
    />
  );
}

export function MorphemeDetailsTileLink(props: MorphemeDetailsTileLinkProps) {
  return props.onNavigate === undefined ? (
    <SelfNavigatingMorphemeDetailsTileLink {...props} />
  ) : (
    <ResolvedMorphemeDetailsTileLink {...props} onNavigate={props.onNavigate} />
  );
}
